// src/shared_new/sections/QuestionsSection.jsx
import React from "react";
import { Box } from "@mui/material";
import SelectedValuesPanel from "../modules/SelectedValuesPanel";
import ChatInterface from "../modules/ChatInterface";

/**
 * QuestionsSection (Presentational Component)
 *
 * Pure UI component for collecting answers through questions
 * Accepts question components as children for maximum flexibility
 *
 * @param {Array} selectedValues - Array of {label, value} objects to display
 * @param {Array} children - Array of React components (questions) to render step-by-step
 * @param {number} currentStep - Current step index
 * @param {function} onStepChange - Callback when step changes (step) => void
 * @param {function} onComplete - Callback when all steps completed
 * @param {string} brandColor - Brand color for theming
 * @param {function} isNextDisabled - Function to determine if next button should be disabled
 * @param {function} onReset - Optional callback for reset button
 * @param {boolean} autoAdvanceOnSelect - Enable auto-advance when selection is made (default: false)
 * @param {boolean} autoAdvanceOnBlur - Enable auto-advance when textbox loses focus (default: false)
 * @param {boolean} demoMode - Optional demo mode state
 * @param {function} onDemoToggle - Optional callback for demo toggle
 */
export default function QuestionsSection({
  selectedValues = [],
  children = [],
  currentStep = 0,
  onStepChange,
  onComplete,
  brandColor = "#ff6034",
  isNextDisabled,
  onReset,
  autoAdvanceOnSelect = false,
  autoAdvanceOnBlur = false,
  demoMode = false,
  onDemoToggle,
}) {
  // Convert children to array if single child
  const questions = React.Children.toArray(children);
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", md: "row" }, // Stack vertically on small screens, side-by-side on medium+
        gap: 3,
        mb: 3,
        height: "100%",
      }}
    >
      {/* Left Side - Selected Values Display */}
      <Box sx={{ flex: 1, minWidth: 0, minHeight: 0 }}>
        <SelectedValuesPanel values={selectedValues} />
      </Box>

      {/* Right Side - Chat Interface with Questions */}
      <Box sx={{ flex: 1, minWidth: 0, minHeight: 0 }}>
        <ChatInterface
          questions={questions}
          currentStep={currentStep}
          onStepChange={onStepChange}
          onComplete={onComplete}
          brandColor={brandColor}
          isNextDisabled={isNextDisabled}
          onReset={onReset}
          autoAdvanceOnSelect={autoAdvanceOnSelect}
          autoAdvanceOnBlur={autoAdvanceOnBlur}
          demoMode={demoMode}
          onDemoToggle={onDemoToggle}
        />
      </Box>
    </Box>
  );
}
